import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';


const services = [
  {
    icon: 'fa-solid fa-cloud-arrow-up',
    title: '1. Legacy Infrastructure Lift & Shift',
    desc: 'Rapidly rehost on-premises physical or virtual workloads directly to cloud environments with minimal application re-architecting and near-zero downtime.',
  },
  {
    icon: 'fa-solid fa-cubes-stacked',
    title: '2. Application Re-Architecting & Refactoring',
    desc: 'Transform monolithic legacy applications into modern microservices to fully leverage cloud-native features, serverless compute, and elasticity.',
  },
  {
    icon: 'fa-solid fa-database',
    title: '3. Database Migration & Modernization',
    desc: 'Seamlessly migrate, replicate, and transform self-hosted databases to cloud-native database services like AWS RDS, Azure SQL, or DynamoDB.',
  },
  {
    icon: 'fa-solid fa-arrows-split-up-and-left',
    title: '4. Multi-Cloud & Hybrid Deployment',
    desc: 'Design and implement hybrid-cloud and multi-cloud strategies across AWS, Azure, and Google Cloud to eliminate vendor lock-in and optimize latency.',
  },
  {
    icon: 'fa-solid fa-shield-halved',
    title: '5. Cloud Security & Compliance Setup',
    desc: 'Embed zero-trust access controls, end-to-end data encryption, and automated compliance frameworks during and after your cloud transition.',
  },
  {
    icon: 'fa-solid fa-coins',
    title: '6. Cloud FinOps & Cost Optimization',
    desc: 'Conduct detailed cost projections, implement auto-scaling rules, reserved instances, and resource tagging to maximize cloud ROI.',
  },
  {
    icon: 'fa-solid fa-arrows-rotate',
    title: '7. DevOps Pipeline Cloud Integration',
    desc: 'Automate deployment pipelines and infrastructure configuration using Terraform, Ansible, Docker, and CI/CD tools during cloud onboarding.',
  },
  {
    icon: 'fa-solid fa-hard-drive',
    title: '8. Cloud Storage & Backup Migration',
    desc: 'Transfer enterprise file systems, SAN/NAS setups, and cold storage archives into secure, durable cloud storage systems.',
  },
  {
    icon: 'fa-solid fa-network-wired',
    title: '9. SD-WAN & Network Connectivity Integration',
    desc: 'Establish ultra-fast, encrypted hybrid connections such as AWS DirectConnect or Azure ExpressRoute for hybrid cloud setups.',
  },
  {
    icon: 'fa-solid fa-headset',
    title: '10. Post-Migration Monitoring & Support',
    desc: 'Provide 24/7 proactive monitoring, health tracking, and system performance fine-tuning immediately following migration go-live.',
  },
];

const whyChooseUs = [
  'Proven 6R Migration Strategy methodology',
  'Near-zero downtime data & server transfers',
  'AWS, Azure, and Google Cloud Certified experts',
  'End-to-end data encryption & compliance guarantee',
  'Up to 40% reduction in infrastructure TCO',
  'Automated Infrastructure as Code (IaC) deployment',
  'Transparent step-by-step migration roadmaps',
  'Minimal disruption to live business operations',
  'Comprehensive post-migration SLA support',
  'FinOps-driven continuous cost optimization',
];

const processSteps = [
  { step: '01', title: 'Assessment & Readiness', desc: 'Audit existing server fleets, dependency mapping, bandwidth, and application compatibility.' },
  { step: '02', title: 'Strategy & Architecture', desc: 'Determine the optimal 6R migration strategy (Rehost, Replatform, Refactor, etc.) and cloud design.' },
  { step: '03', title: 'Landing Zone Setup', desc: 'Build secure, compliant cloud environments complete with identity management, networking, and firewalls.' },
  { step: '04', title: 'Pilot Migration', desc: 'Execute dry-run migrations for non-critical workloads to validate performance, security, and integrity.' },
  { step: '05', title: 'Full Cutover', desc: 'Migrate core applications and databases using automated replication tools during low-traffic windows.' },
  { step: '06', title: 'Optimization & Handover', desc: 'Fine-tune auto-scaling metrics, track spending, monitor telemetry, and train internal engineering teams.' },
];

const techStack = [
  { category: 'Cloud Providers', items: ['AWS', 'Microsoft Azure', 'Google Cloud Platform (GCP)', 'DigitalOcean'] },
  { category: 'Migration & Replication', items: ['AWS Migration Hub', 'Azure Migrate', 'Carbonite', 'CloudEndure', 'Rivet'] },
  { category: 'Containerization & Orchestration', items: ['Docker', 'Kubernetes', 'Amazon ECS', 'Azure AKS'] },
  { category: 'IaC & Management', items: ['Terraform', 'Ansible', 'CloudFormation', 'Datadog', 'New Relic'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const CloudMigration = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Cloud Engineering
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Cloud <span className="gradient-text-cyan">Migration</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we simplify your digital evolution. Move your workloads, databases, and enterprise applications to the cloud seamlessly, securely, and with zero operational disruption.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Start Your Cloud Journey With Lidlbritain &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Cloud Migration Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Migrate with <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Platform Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Cloud Ecosystem & Tools</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Cloud Framework</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Accelerate Your Cloud Transformation with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Partner with Lidlbritain cloud architects to design a tailored migration roadmap that drives innovation, cuts operational overhead, and unleashes agility.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Request a Free Cloud Migration Audit &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CloudMigration;